import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CL_DEFAULT_FILTERS, COMM_LOG, CommLogView, reconcileFilters } from '@elva/ui';
import type { ClAgent, ClChannel, ClDirection, ClFilters, ClRange, VerdictFilter } from '@elva/ui';
import { useClearPageTopBarActions } from '@/app/layouts/pageTopBar';

const DIRECTIONS = new Set<string>(['inbound', 'outbound']);
const CHANNELS = new Set<string>(['text', 'email', 'call']);
const RANGES = new Set<string>(['today', '24h', '7d', '30d', 'custom']);
const AGENTS = new Set<string>([
  'receptionist',
  'reminders',
  'recall',
  'gap-filler',
  'treatment',
  'invisalign',
  'ar',
]);
const VERDICTS = new Set<string>([
  'sent',
  'held',
  'skipped',
  'blocked',
  'suppressed',
  'failed',
  'chose-not',
  'booked',
  'moved',
  'answered',
  'message',
  'warm_transfer',
  'urgent_triage',
  'cancelled',
]);

/**
 * Read the filters out of the URL (spec 18 §5).
 *
 * **An unknown or impossible value is ignored with the filter shown as unset**,
 * never silently coerced into something else — a URL somebody mistyped should show
 * them everything, not a plausible-looking subset they did not ask for.
 */
function fromUrl(params: URLSearchParams): ClFilters {
  const pick = <T extends string>(key: string, allowed: Set<string>, fallback: T): T => {
    const v = params.get(key);
    return v !== null && allowed.has(v) ? (v as T) : fallback;
  };
  return reconcileFilters({
    direction: pick<'all' | ClDirection>('direction', DIRECTIONS, 'all'),
    agent: pick<'all' | ClAgent>('agent', AGENTS, 'all'),
    channel: pick<'all' | ClChannel>('channel', CHANNELS, 'all'),
    verdict: pick<VerdictFilter>('verdict', VERDICTS, 'all'),
    reason: params.get('reason') ?? 'all',
    range: pick<ClRange>('range', RANGES, CL_DEFAULT_FILTERS.range),
    query: params.get('q') ?? '',
  });
}

/** Only non-default values go in, so a plain `/communications` stays plain. */
function toUrl(f: ClFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (f.direction !== 'all') p.set('direction', f.direction);
  if (f.agent !== 'all') p.set('agent', f.agent);
  if (f.channel !== 'all') p.set('channel', f.channel);
  if (f.verdict !== 'all') p.set('verdict', f.verdict);
  if (f.reason !== 'all') p.set('reason', f.reason);
  if (f.range !== CL_DEFAULT_FILTERS.range) p.set('range', f.range);
  if (f.query !== '') p.set('q', f.query);
  return p;
}

/**
 * Communication log (spec 11, amended by spec 18) — **one row per contact, inbound
 * and outbound.**
 *
 * With Reports dropped from V1 this is the only record surface in the product, and
 * every filter round-trips through the URL so the Dashboard's promise that *every
 * number opens the rows behind it* is a link rather than a sequence of clicks the
 * user performs on arrival.
 *
 * §8.1: the header action slot is empty on this route — nothing here is sent, so
 * there is no top-bar action, and no compose, reply or resend path exists.
 */
export function CommunicationLogPage() {
  useClearPageTopBarActions();
  const [params, setParams] = useSearchParams();

  const onFiltersChange = useCallback(
    (next: ClFilters) => {
      setParams(toUrl(next));
    },
    [setParams],
  );

  return <CommLogView data={COMM_LOG} filters={fromUrl(params)} onFiltersChange={onFiltersChange} />;
}
